import { useFrame, useThree } from '@react-three/fiber';
import { RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Group, PerspectiveCamera, Vector3, Box3, Scene, Vector2, Color, Object3DEventMap, Object3D } from 'three';
import { getScreenRelativeContentPosition } from '../ImageTrackerWrapper/utils';
import { ITuple3 } from '@/components/r3f-components/component-data-structure';
import { Transition, IEasingTypes } from '@/components/r3f-components/transitions';
import { useGetCurrentSceneId } from '@/hooks/general';
import { Jute } from '@/index';
import { zapparCameraInstance } from '@/components/ZapparCameraWrapper';
import { useDataStore, useSceneStore } from '@/zustand';

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { IWorldTrackingMode } from './WorldTrackingPlacementUI/WorldTrackingPlacementUI';

export const usePlaneLostBehaviour = (trackerContentGroupRef: RefObject<Group | null>, childGroupRef: RefObject<Group | null>, lerp?: boolean) => {
	const { scene, gl: renderer } = useThree();
	const sceneId = useGetCurrentSceneId();
	const localInnerGroupRef = useRef<Group>(undefined);

	const isEnterTransitionRunningRef = useRef(false);
	const isExitTransitionRunningRef = useRef(false);

	const screenRelativePositionRef = useRef<Vector3>(undefined);

	useFrame(() => {
		if (isEnterTransitionRunningRef.current) enterTransition.tick();
		if (isExitTransitionRunningRef.current) exitTransition.tick();
	});

	const enterTransition = useMemo(
		() =>
			new Transition<ITuple3>({ start: [0, 0, 0], end: [0, 0, 0], duration: 0, easingType: IEasingTypes.easeInOutQuad, delay: 0, isTestMode: false, transitionId: `planeLostTransition-${sceneId}` }),
		[]
	);

	const exitTransition = useMemo(
		() =>
			new Transition<ITuple3>({ start: [0, 0, 0], end: [0, 0, 0], duration: 0, easingType: IEasingTypes.easeInOutQuad, delay: 0, isTestMode: false, transitionId: `planeLostTransition-${sceneId}` }),
		[]
	);

	const enterPlaneLostBehaviour = (disableAnimation?: boolean) => {
		isExitTransitionRunningRef.current = false;
		isEnterTransitionRunningRef.current = true;

		const trackerContent = trackerContentGroupRef.current;

		const zapparCamera = zapparCameraInstance;

		if (!zapparCamera || !trackerContent) {
			//console.log('plane lost enter returned early', {zapparCamera, trackerContent})
			return;
		}

		const aspect = renderer.domElement.width / renderer.domElement.height;

		let contentStartPosition: Vector3 | undefined;

		if (lerp && !disableAnimation) {
			// Get content start position before removed from scene graph
			const contentWorldPosition = trackerContent.getWorldPosition(new Vector3());
			contentStartPosition = zapparCamera.worldToLocal(contentWorldPosition);
		}

		// // Remove trackerContent from tracker group
		trackerContent.removeFromParent();

		if (!screenRelativePositionRef.current) {
			// Calculate the screen relative position
			const bbox = new Box3().setFromObject(trackerContent);
			const contentScale = bbox.getSize(new Vector3());
			const contentCenter = bbox.getCenter(new Vector3());
			screenRelativePositionRef.current = getScreenRelativeContentPosition(zapparCamera as unknown as PerspectiveCamera, contentScale, contentCenter, aspect);
		}

		if (!lerp || disableAnimation) {
			trackerContent.position.copy(screenRelativePositionRef.current);
			zapparCamera.add(trackerContent);
			return;
		}

		zapparCamera.add(trackerContent);

		enterTransition
			.reset()
			.set({ from: contentStartPosition!.toArray(), to: screenRelativePositionRef.current.toArray(), duration: 250, delay: 0 })
			.onChange((p) => trackerContent.position.set(...p))
			.onFinish(() => (isEnterTransitionRunningRef.current = false))
			.start();
	};

	const exitPlaneLostBehaviour = (disableAnimation: boolean) => {
		isEnterTransitionRunningRef.current = false;
		isExitTransitionRunningRef.current = true;

		const arScene = scene.getObjectByName('ArScene') as Scene | undefined;
		const trackerContent = trackerContentGroupRef.current;
		// const trackerInnerGroup = arScene?.getObjectByName('wt-inner-group');

		if (!localInnerGroupRef.current) {
			localInnerGroupRef.current = arScene?.getObjectByName('wt-inner-group') as Group;
			if (!localInnerGroupRef.current) localInnerGroupRef.current = childGroupRef.current?.parent as Group | undefined;
		}

		const trackerInnerGroup = localInnerGroupRef.current;

		if (!trackerContent || !trackerInnerGroup) {
			//console.log('early return plane lost exit', {trackerContent, trackerInnerGroup})
			return;
		}

		let contentStartPosition: Vector3 | undefined;

		if (lerp && !disableAnimation) {
			// Get the world screen relative position and convert to local space of tracker group
			const contentWorldPosition = trackerContent.getWorldPosition(new Vector3());
			contentStartPosition = trackerInnerGroup.worldToLocal(contentWorldPosition);
		}

		// The end position is at origin of tracker group
		const zeroPosition = new Vector3(0);

		// Now remove content from zappar camera and add to tracker group at equivalent position and lerp to tracker group origin
		trackerContent.removeFromParent();
		trackerContent.position.copy(contentStartPosition ?? zeroPosition);
		trackerInnerGroup.add(trackerContent);

		if (lerp && !disableAnimation) {
			exitTransition
				.reset()
				.set({ from: contentStartPosition!.toArray(), to: zeroPosition.toArray(), duration: 250, delay: 0 })
				.onChange((p) => trackerContent.position.set(...p))
				.onFinish(() => (isExitTransitionRunningRef.current = false))
				.start();
		}
	};

	return { enterPlaneLostBehaviour, exitPlaneLostBehaviour };
};

interface IUseIntersectionHandlersReturnValues {
	isIntersectingRef: React.RefObject<boolean>;
	isNotIntersectingRef: React.RefObject<boolean>;
}

export const useIntersectionHandlers = (
	childGroupRef: React.RefObject<Group | null>,
	onPlaneIntersectionFound: () => unknown,
	onPlaneIntersectionNotFound: (animate: boolean) => unknown
): IUseIntersectionHandlersReturnValues => {
	const customAnchorCreatedRef = useRef(false);
	const lastEventWasIntersectionFoundRef = useRef(false);
	const lastEventWasIntersectionNotFoundRef = useRef(false);
	const { scene } = useThree();
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const arScene = scene.getObjectByName('ArScene') as Scene | undefined;
	const localInnerGroupRef = useRef<Group>(undefined);

	// Called per frame when inner group is visible & inner group has been visible once before
	// - Returns when last event was 'intersection found'
	const _onPlaneIntersectionFound = () => {
		if (lastEventWasIntersectionFoundRef.current) return;
		// console.log('plane: intersection FOUND ONCE')
		lastEventWasIntersectionFoundRef.current = true;
		lastEventWasIntersectionNotFoundRef.current = false;
		onPlaneIntersectionFound();
	};

	// Called per frame when inner group is not visible
	// - Returns when last event was "intersection not found"
	const _onPlaneIntersectionNotFound = () => {
		if (lastEventWasIntersectionNotFoundRef.current) return;
		lastEventWasIntersectionNotFoundRef.current = true;
		// console.log('plane: intersection LOST ONCE')
		onPlaneIntersectionNotFound(lastEventWasIntersectionFoundRef.current);
		lastEventWasIntersectionFoundRef.current = false;
	};

	useFrame(() => {
		if ((!Jute.isAppClip && !Jute.isMobileWebXRExperience) || !placementMode) {
			//console.log('[useFrame]: early return', {wtNiotEnabled: !Jute.isAppClip || !Jute.isMobileWebXRExperience, placementMode})
			return;
		}

		if (!localInnerGroupRef.current) {
			localInnerGroupRef.current = arScene?.getObjectByName('wt-inner-group') as Group;
			if (!localInnerGroupRef.current) localInnerGroupRef.current = childGroupRef.current?.parent as Group | undefined;
		}

		const innerGroup = localInnerGroupRef.current;

		if (!innerGroup) {
			// console.log('[useFrame]: early return attempt2', childGroupRef.current)
			return;
		}

		if (!innerGroup.visible) {
			_onPlaneIntersectionNotFound();
			// console.log('[useFrame]: intersection not found: group invisble', {innerGroup})
			return;
		}

		if (innerGroup.visible && customAnchorCreatedRef.current) {
			_onPlaneIntersectionFound();
			// console.log('[useFrame]: innerGroup', innerGroup)
			return;
		}

		if (innerGroup.visible && !customAnchorCreatedRef.current) {
			// console.log('[useFrame]: custom anchor created')
			customAnchorCreatedRef.current = true;
		}
	});

	return { isIntersectingRef: lastEventWasIntersectionFoundRef, isNotIntersectingRef: lastEventWasIntersectionNotFoundRef };
};

export const useShowInitializationUI = (isInitializing: boolean) => {
	const [showInitializationUi, setShowInitializationUi] = useState(false);
	const [isJuteReady, setIsJuteReady] = useState(Jute.isReady);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	useLayoutEffect(() => {
		Jute.onReady(() => setIsJuteReady(true));
	}, []);

	useEffect(() => {
		if (!Jute.isAppClip && !Jute.isMobileWebXRExperience) return;
		if (!isInitializing) {
			clearTimeout(timeoutRef.current);
			setShowInitializationUi(false);
			return;
		}

		if (Jute.isAppClip) {
			timeoutRef.current = setTimeout(() => {
				setShowInitializationUi(true);
			}, 1500);
		} else setShowInitializationUi(true);
	}, [isInitializing]);

	return showInitializationUi && isJuteReady;
};

const useOnPlacementPlaneGroupAdded = (fn: (arScene: Scene, planeMeshGroup: Object3D<Object3DEventMap>) => unknown) => {
	const { scene } = useThree();
	const arScene = scene.getObjectByName('ArScene') as Scene;
	const userPlacemementGroup = arScene?.getObjectByName('userPlacementGroup');
	if (userPlacemementGroup?.children[0]) fn(arScene, userPlacemementGroup?.children[0]);
	else if (!userPlacemementGroup)
		arScene.addEventListener('childadded', (e) => {
			if (e.child.name === 'userPlacementGroup') {
				console.log('userPlacementGroup added');
				fn(arScene, e.child.children[0]);
			}
		});
	else
		userPlacemementGroup.addEventListener('childadded', (e) => {
			fn(arScene, e.child);
		});
};

const _placementPlaneGroupAdded = (scene: Scene): Promise<[Scene, Object3D<Object3DEventMap>]> => {
	return new Promise((res) => {
		const arScene = scene.getObjectByName('ArScene') as Scene;
		const userPlacemementGroup = arScene?.getObjectByName('userPlacementGroup');
		if (userPlacemementGroup?.children[0]) res([arScene, userPlacemementGroup?.children[0]]);
		else if (!userPlacemementGroup)
			arScene.addEventListener('childadded', (e) => {
				if (e.child.name === 'userPlacementGroup') {
					console.log('userPlacementGroup added');
					res([arScene, e.child.children[0]]);
				}
			});
		else
			userPlacemementGroup.addEventListener('childadded', (e) => {
				res([arScene, e.child]);
			});
	});
};

const placementPlaneGroupAdded = (scene: Scene): Promise<[Scene, Object3D<Object3DEventMap>]> => {
	return new Promise((resolve) => {
		const arScene = scene.getObjectByName('ArScene') as Scene;
		const userPlacementGroup = arScene?.getObjectByName('userPlacementGroup');

		const cleanup = () => {
			arScene?.removeEventListener('childadded', onGroupAdded);
			userPlacementGroup?.removeEventListener('childadded', onChildAdded);
		};

		const onGroupAdded = (e: any) => {
			if (e.child.name === 'userPlacementGroup') {
				cleanup();
				resolve([arScene, e.child.children[0]]);
			}
		};

		const onChildAdded = (e: any) => {
			cleanup();
			resolve([arScene, e.child]);
		};

		if (userPlacementGroup?.children[0]) {
			resolve([arScene, userPlacementGroup.children[0]]);
			return;
		}

		if (!userPlacementGroup) {
			arScene.addEventListener('childadded', onGroupAdded);
			return;
		}

		userPlacementGroup.addEventListener('childadded', onChildAdded);
	});
};

export const useOutlineGlow = (enabled: boolean, mode: IWorldTrackingMode) => {
	const { scene, camera, gl } = useThree();

	const composerRef = useRef<EffectComposer>(null);
	const outlinePassRef = useRef<OutlinePass>(null);
	const renderPassRef = useRef<RenderPass>(null);
	const outputPassRef = useRef<OutputPass>(null);
	const isPassAddedRef = useRef(false);
	const isIntersecting = mode === 'intersecting' || mode === 'intersecting-no-animation';
	const isSearching = mode === 'searching' || mode === 'searching-no-animation';

	const cleanupRenderPasses = () => {
		outlinePassRef.current?.dispose();
		outputPassRef.current?.dispose();
		composerRef.current?.dispose();

		outlinePassRef.current = null;
		outputPassRef.current = null;
		renderPassRef.current = null;
		composerRef.current = null;
	};

	useEffect(() => {
		if (isPassAddedRef.current || !isSearching || !enabled) return;
		isPassAddedRef.current = true;

		const addOutlinePass = async () => {
			cleanupRenderPasses();
			const [arScene, placementPlaneGroup] = await placementPlaneGroupAdded(scene);

			placementPlaneGroup.renderOrder = -Infinity;
			composerRef.current = new EffectComposer(gl);
			renderPassRef.current = new RenderPass(arScene, camera);
            outputPassRef.current = new OutputPass();

			outlinePassRef.current = new OutlinePass(new Vector2(gl.domElement.width, gl.domElement.height), arScene, camera);
			outlinePassRef.current.edgeThickness = 2.0;
			outlinePassRef.current.edgeGlow = 0.5;
			outlinePassRef.current.edgeStrength = 3.0;
			outlinePassRef.current.hiddenEdgeColor = new Color('#000000');
			outlinePassRef.current.selectedObjects = [placementPlaneGroup];

			composerRef.current.addPass(renderPassRef.current);
			composerRef.current.addPass(outlinePassRef.current);
			composerRef.current.addPass(outputPassRef.current);
		};

		addOutlinePass();
	}, [isSearching, enabled]);

	useEffect(() => {
		return () => cleanupRenderPasses();
	}, []);

	useEffect(() => {
		if (!outlinePassRef.current) return;
		outlinePassRef.current.visibleEdgeColor?.set(isIntersecting ? '#ffffff' : '#969595');
	}, [isIntersecting]);

	useFrame(() => {
		if (!enabled || (!isSearching && !isIntersecting)) return;
		composerRef.current?.render();
	}, 11);
};
